import type { Dayjs } from "dayjs";

import DurationService from "app/services/DurationService";
import dayjs from "single-spa/dayjs-extend";

const API_DATE_FORMAT = "YYYY-MM-DD";

export interface DateRange {
    from: string;
    to: string;
}

export interface CalendarDateLimits {
    minDate: Dayjs;
    maxDate: Dayjs;
    minDailyDate: Dayjs;
    maxDailyDate: Dayjs;
}

/**
 * Formats a dayjs date to API format (YYYY-MM-DD).
 * This format is timezone-agnostic and prevents date shifting issues.
 */
export function formatDateForApi(date: Dayjs): string {
    return date.format(API_DATE_FORMAT);
}

/**
 * Parses a duration string (e.g., "2024.01.18-2024.01.19" or "28d") into a date range.
 * Returns dates in YYYY-MM-DD format to avoid timezone issues.
 */
export function parseDurationToDateRange(duration: string): DateRange {
    const today = dayjs();

    if (duration?.includes("-")) {
        const [from, to] = duration.split("-");
        return {
            from: dayjs(from.replace(/\./g, "-")).format(API_DATE_FORMAT),
            to: dayjs(to.replace(/\./g, "-")).format(API_DATE_FORMAT),
        };
    }

    const parsed = DurationService.parsedDurationParam(duration) as { unit: string; count: number };
    const fromDate = today.subtract(parsed.count, parsed.unit === "D" ? "day" : "month");

    return {
        from: formatDateForApi(fromDate),
        to: formatDateForApi(today),
    };
}

/**
 * Gets the minimum date for the daily calendar picker.
 * Uses startOf("day") to ensure consistent date boundaries.
 */
export function getCalendarMinDailyDate(daysBack: number): Dayjs {
    return dayjs().subtract(daysBack, "days").startOf("day");
}

/**
 * Gets the maximum date for the daily calendar picker.
 * Uses UTC and endOf("day") to match the calendar's internal UTC date representation.
 * This ensures today's date is selectable in the calendar.
 */
export function getCalendarMaxDailyDate(): Dayjs {
    return dayjs().utc().endOf("day");
}

/**
 * Gets all date limits for the calendar picker.
 */
export function getCalendarDateLimits(options: {
    isDailyCalendar: boolean;
    daysBack?: number;
    monthsBack?: number;
}): CalendarDateLimits {
    const { isDailyCalendar, daysBack = 28, monthsBack = 12 } = options;

    const minDate = isDailyCalendar ? getCalendarMinDailyDate(daysBack) : dayjs().subtract(monthsBack, "months");

    const maxDate = dayjs().endOf("day");
    const maxDailyDate = getCalendarMaxDailyDate();

    return {
        minDate,
        maxDate,
        minDailyDate: getCalendarMinDailyDate(daysBack),
        maxDailyDate,
    };
}

/**
 * Creates a date in UTC at the start of the day.
 * Useful for consistent date comparisons with calendar components.
 */
export function toUtcStartOfDay(date: Dayjs | string): Dayjs {
    return dayjs(date).utc().startOf("day");
}

/**
 * Creates a date in UTC at the end of the day.
 * Useful for setting inclusive max date boundaries.
 */
export function toUtcEndOfDay(date: Dayjs | string): Dayjs {
    return dayjs(date).utc().endOf("day");
}

/**
 * Checks if a date is within the valid range for the daily calendar.
 * Uses local time comparison (not UTC) for business logic validation.
 */
export function isDateInDailyRange(date: Dayjs, daysBack: number = 28): boolean {
    const minDate = dayjs().subtract(daysBack, "days").startOf("day");
    const today = dayjs().endOf("day");

    return !date.isBefore(minDate, "day") && !date.isAfter(today, "day");
}
